import Tippy from '@tippyjs/react';
import { CSSProperties, memo, useEffect, useMemo, useRef } from 'react';
import { DateRangePicker, createStaticRanges } from 'react-date-range';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import styled, { createGlobalStyle } from 'styled-components';

import { ModalDatePicker } from 'app/components/Filter/InputDateFilter';
import Typography from 'app/components/Typography';

import { staticDateRanges } from 'app/modules/system-settings/configuration/core/constants';

import { ReactComponent as IconDate } from 'app/assets/icons/icon-date.svg';

import type { Instance } from 'tippy.js';

const GlobalPopoverStyle = createGlobalStyle`
  .tippy-box[data-theme~='visual-archive-date-popper'] {
    box-shadow: 23.4866px 32.3787px 94px rgba(0, 0, 0, 0.1),
      4.69732px 6.47574px 15.275px rgba(0, 0, 0, 0.05);
    border-radius: 8px;
    max-width: unset !important;
    background: var(--white);
    z-index: 9999;
  }
  .tippy-box[data-theme~='visual-archive-date-popper'] .tippy-content {
    padding: 0;
  }
`;

const InputDate = styled.div`
  height: 43px;
  background: var(--neutral-2);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0px 12px;
  cursor: pointer;
`;

const CloseButton = styled.button`
  position: absolute;
  bottom: 12px;
  right: 12px;
  height: 36px;
  padding: 0px 14px;
  border: 1px solid var(--neutral-3);
  background: var(--white);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 99999;
  color: var(--neutral-10);
  font-weight: 600;
  font-size: 14px;
  line-height: 23px;

  &:hover {
    background: var(--neutral-2-5);
  }

  border-radius: 8px;
`;

function findNearestScrollParent(element: HTMLElement | null): HTMLElement | null {
  let current: HTMLElement | null = element;
  while (current && current !== document.body) {
    const style = window.getComputedStyle(current);
    const overflowY = style.overflowY;
    const overflowX = style.overflowX;
    const isScrollable =
      (overflowY === 'auto' ||
        overflowY === 'scroll' ||
        overflowX === 'auto' ||
        overflowX === 'scroll') &&
      (current.scrollHeight > current.clientHeight || current.scrollWidth > current.clientWidth);
    if (isScrollable) return current;
    current = current.parentElement;
  }
  return null;
}

function findOuterFilterPopover(element: HTMLElement | null): HTMLElement | null {
  if (!element) return null;
  return element.closest(
    ".tippy-box[data-theme~='visual-archive-filter-popper']",
  ) as HTMLElement | null;
}

interface Props {
  label?: string;
  value: string;
  onChange: (
    newDate: {
      startDate: Date;
      endDate: Date;
      key: string;
    }[],
  ) => void;
  state: {
    startDate: Date;
    endDate: Date;
    key: string;
  }[];
  onClear?: () => void;
  containerStyle?: CSSProperties;
  inputDateStyle?: CSSProperties;
}

const VisualArchiveInputDateFilter: React.FC<Props> = ({
  label,
  value,
  onChange,
  state,
  onClear,
  containerStyle,
  inputDateStyle,
}) => {
  const triggerRef = useRef<HTMLDivElement | null>(null);
  const tippyInstanceRef = useRef<Instance | null>(null);

  const popperOptions = useMemo(() => {
    return {
      strategy: 'fixed' as const,
      modifiers: [
        {
          name: 'preventOverflow',
          options: {
            altAxis: true,
            tether: false,
            boundary: 'viewport',
          },
        },
        {
          name: 'flip',
          options: {
            fallbackPlacements: ['bottom', 'top', 'right', 'left'],
          },
        },
        {
          name: 'computeStyles',
          options: {
            adaptive: false,
            gpuAcceleration: false,
          },
        },
      ],
    };
  }, []);

  useEffect(() => {
    const update = () => tippyInstanceRef.current?.popperInstance?.update();
    const scrollParent = findNearestScrollParent(triggerRef.current);

    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    if (scrollParent) scrollParent.addEventListener('scroll', update, { passive: true });

    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
      if (scrollParent) scrollParent.removeEventListener('scroll', update as any);
    };
  }, []);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        ...containerStyle,
      }}
    >
      <GlobalPopoverStyle />
      <div
        className="d-flex align-items-center justify-content-between"
        style={{
          marginBottom: 4,
        }}
      >
        <Typography fontWeight={700} fontSize={14} lineHeight={23} color="gray-700">
          {label}
        </Typography>
        {onClear && (
          <Typography
            variant="body-3"
            color="neutral-6"
            style={{ cursor: 'pointer' }}
            onClick={onClear}
          >
            Clear
          </Typography>
        )}
      </div>

      <Tippy
        interactive
        arrow={false}
        placement="top-end"
        offset={[27, -5]}
        trigger="click"
        allowHTML
        appendTo={() => findOuterFilterPopover(triggerRef.current) ?? document.body}
        popperOptions={popperOptions}
        theme="visual-archive-date-popper"
        onMount={(instance) => {
          tippyInstanceRef.current = instance;
          instance.popperInstance?.update();
        }}
        onHidden={() => {
          tippyInstanceRef.current = null;
        }}
        content={
          <ModalDatePicker style={{ position: 'relative' }}>
            <CloseButton
              type="button"
              aria-label="Close date picker"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                tippyInstanceRef.current?.hide();
              }}
            >
              Close
            </CloseButton>
            <DateRangePicker
              onChange={(item) => {
                onChange([item.selection as any]);
              }}
              showPreview={true}
              months={2}
              staticRanges={[...createStaticRanges(staticDateRanges() as any)]}
              ranges={state}
              direction="horizontal"
              rangeColors={['var(--primary)']}
            />
          </ModalDatePicker>
        }
      >
        <InputDate
          ref={triggerRef}
          style={{
            marginBottom: 15,
            ...inputDateStyle,
          }}
        >
          <Typography variant="body-5" color={value ? 'neutral-10' : 'neutral-6'}>
            {value ? value : label}
          </Typography>
          <IconDate />
        </InputDate>
      </Tippy>
    </div>
  );
};

export default memo(VisualArchiveInputDateFilter);
